import React, { FunctionComponent, useContext } from 'react';
import Moment from 'react-moment';
import { Link, useLocation } from 'react-router-dom';

import { MessageContext } from '../../contexts/messageContext';
import { MessageContentLoader } from '../ContentLoader';
import { useTabbedRoomListsContext } from '../../contexts/tabbedRoomListsContext';
import InfiniteScroll from 'react-infinite-scroll-component';
import Spinner from '../Spinner';
import { getCompressedImagePath } from '../../utils/getCompressedImagePath';
import ChatTabs from './ChatTabs';

interface ITabbedMessageUserListProps {
  setOpenRoom: React.Dispatch<React.SetStateAction<boolean>>;
}

const MAX_RECENT_MESSAGE_LENGTH = 81;

const TabbedMessageUserList: FunctionComponent<ITabbedMessageUserListProps> = (props) => {
  const { actions } = useContext(MessageContext);
  const {
    rooms,
    loading,
    error,
    handleFetchNextRoomPage,
    hasMoreRoomsToFetch
  } = useTabbedRoomListsContext();

  const location = useLocation();
  const [, roomId] = location.pathname.split('/messages/');
  const { setOpenRoom } = props;

  if (loading) {
    return (
      <div className="p-t-1 p-b-1 p-r-1 p-l-1">
        <MessageContentLoader />
        <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '12px', color: '#666' }}>
          Loading chats...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-t-1 p-b-1 p-r-1 p-l-1">
        <p>Something went wrong</p>
        <button onClick={() => window.location.reload()}>Reload Page</button>
      </div>
    );
  }

  if (!rooms || rooms.length === 0) {
    return (
      <>
        <ChatTabs />
        <div className="p-t-1 p-b-1 p-r-1 p-l-1">
          <p style={{ textAlign: 'center', color: '#666' }}>No chats available</p>
        </div>
      </>
    );
  }

  return (
    <>
      <ChatTabs />
      <InfiniteScroll
        dataLength={rooms.length}
        hasMore={hasMoreRoomsToFetch}
        loader={<Spinner />}
        scrollThreshold={0.1}
        next={async () => await handleFetchNextRoomPage()}
        scrollableTarget="scrollableDiv"
      >
        {rooms.map((r, k) => {
          const otherUser = actions.getOtherUser(r.members);
          return (
            <li
              key={k}
              className={`page-messages-column-list-item p-t-1 p-b-1 p-r-1 p-l-1 ${
                roomId === r.id ? 'selected' : ''
              }`}
            >
              <Link className="is-flex column-gap-1" to={`/messages/${r.id}`} onClick={() => setOpenRoom(true)}>
                <img
                  src={getCompressedImagePath(otherUser?.avatar?.fileUrl, 'thumbnail') ?? otherUser?.avatar?.fileUrl}
                  alt="profile placeholder"
                  className="image is-inline is-rounded-circle is-42x42"
                />
                <div className='flex-1'>
                  <div className="level m-b-0 is-flex">
                    <div className="level-left">
                      <div className="level-item content">
                        <p
                          className={`page-messages-column-list-item-sender m-b-0 is-small page-messages-column-list-item-title ${
                            r?.hasUnreadMessage ? 'unread' : ''
                          }`}
                        >
                          {otherUser && otherUser.profile && otherUser.profile.name}
                          {r.chatType === 'coffee' && (
                            <span className="tag is-primary is-small coffee-chat-badge ml-1">Coffee Chat</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="level-right m-t-0">
                      <div className="level-item">
                        <span className="content is-small page-messages-column-list-item-time">
                          <Moment fromNow>
                            {r.createdAt}
                          </Moment>
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="page-messages-column-list-item-content is-small text-ellipsis">
                    {r.recentMessage
                      ? r.recentMessage.length > MAX_RECENT_MESSAGE_LENGTH
                        ? `${r.recentMessage.substr(
                            0,
                            MAX_RECENT_MESSAGE_LENGTH
                          )}...`
                        : r.recentMessage
                      : '......'}
                  </p>
                </div>
              </Link>
            </li>
          );
        })}
      </InfiniteScroll>
    </>
  );
};

export default TabbedMessageUserList;